import React, { useState, useMemo, useCallback } from "react";
import LayoutHOC from "../LayoutHOC";
import { Link } from "react-router-dom";
import useCandidate from "../../hooks/useCandidate";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  usePagination,
  useSortBy,
} from "react-table";
import { useSelector } from "react-redux";
import { ACCOUNT_STATUS, DELETE_CANDIDATE } from "../../utils/constants";

const StatusToggle = ({ value, id, token }) => {
  const [isActive, setIsActive] = useState(value == 1);

  const handleClick = async () => {
    const newValue = !isActive;
    setIsActive(newValue);
    try {
      const formData = new FormData();
      formData.append("status", newValue ? 1 : 0);
      await fetch(`${ACCOUNT_STATUS}${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`relative w-12 h-6 rounded-full transition-colors ${isActive ? "bg-emerald-500" : "bg-gray-300"}`}
    >
      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${isActive ? "left-7" : "left-1"}`}></span>
    </button>
  );
};

const OTPBadge = ({ value }) => {
  const isVerified = value === "verified";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
      isVerified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isVerified ? "bg-emerald-500" : "bg-amber-500"}`}></span>
      {isVerified ? "Verified" : "Pending"}
    </span>
  );
};

const Candidate = () => {
  const positions = useCandidate();
  const [successMessage, setSuccessMessage] = useState("");
  const user = useSelector((state) => state.user);

  const handleDelete = useCallback(async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this candidate?");
    if (confirmDelete) {
      try {
        const { token } = user;
        const response = await fetch(`${DELETE_CANDIDATE}${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          setSuccessMessage("Candidate deleted successfully!");
          setTimeout(() => {
            setSuccessMessage("");
            window.location.reload();
          }, 1500);
        } else {
          alert("Failed to delete candidate. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting:", error);
        alert("Failed to delete candidate. Please try again.");
      }
    }
  }, [user]);

  const columns = useMemo(
    () => [
      {
        Header: "Candidate Info",
        accessor: "candidateInfo",
        Cell: ({ cell: { row } }) => {
          const { profilepic, email, name } = row.original;
          return (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center overflow-hidden">
                {profilepic ? (
                  <img src={profilepic} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <i className="bi bi-person text-teal-500"></i>
                )}
              </div>
              <div>
                <div className="font-medium text-gray-900">{name || 'Unknown'}</div>
                <div className="text-sm text-gray-500">{email}</div>
                <div className="text-sm text-gray-500">{row.original.mobile}</div>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Job Position",
        accessor: "jobposition",
        sortType: "alphanumeric",
        Cell: ({ value }) => (
          <span className="text-gray-600">{value || 'Not specified'}</span>
        ),
      },
      {
        Header: "Applied Jobs",
        accessor: "jobsapplied",
        sortType: "alphanumeric",
        Cell: ({ value }) => (
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-medium text-sm">
            {value || 0}
          </span>
        ),
      },
      {
        Header: "Account Status",
        accessor: "status",
        sortType: "alphanumeric",
        Cell: ({ row }) => (
          <StatusToggle value={row.original.status} id={row.original.id} token={user?.token} />
        ),
      },
      {
        Header: "OTP Status",
        accessor: "otp_verified",
        sortType: "alphanumeric",
        Cell: ({ row }) => <OTPBadge value={row.original.otp_verified} />,
      },
      {
        Header: "Joined",
        accessor: "created_at",
        sortType: "alphanumeric",
        Cell: ({ value }) => (
          <span className="text-gray-600 text-sm">{value || 'N/A'}</span>
        ),
      },
      {
        Header: "Actions",
        accessor: "id",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Link
              to={`/view_profile/${row.values.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-eye"></i>
              View
            </Link>
            <Link
              to={`/edit_candidate/${row.values.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-pencil"></i>
              Edit
            </Link>
            <button
              onClick={() => handleDelete(row.values.id)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
            >
              <i className="bi bi-trash"></i>
              Delete
            </button>
          </div>
        ),
      },
    ],
    [handleDelete, user?.token]
  );

  const data = useMemo(() => positions || [], [positions]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    setPageSize,
  } = useTable(
    { columns, data, initialState: { pageSize: 10, sortBy: [{ id: "created_at", desc: true }] } },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg">
                <i className="bi bi-people text-white text-2xl"></i>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-teal-800 to-cyan-600 bg-clip-text text-transparent">
                  Candidates
                </h1>
                <p className="text-gray-500">Manage job seekers and their profiles</p>
              </div>
            </div>
            <Link
              to="/create_candidate"
              className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl hover:from-teal-600 hover:to-cyan-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
            >
              <i className="bi bi-plus-lg"></i>
              Add Candidate
            </Link>
          </div>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
            <i className="bi bi-check-circle-fill text-emerald-500 text-xl"></i>
            <span className="text-emerald-700 font-medium">{successMessage}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
            <div className="relative">
              <i className="bi bi-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search candidates..."
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl w-72 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-gray-500">entries</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" {...getTableProps()}>
              <thead className="bg-gradient-to-r from-teal-50 to-cyan-50">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-teal-100/50 transition-colors"
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                      >
                        <div className="flex items-center gap-2">
                          {column.render("Header")}
                          <span className="text-teal-400">
                            {column.isSorted ? (column.isSortedDesc ? "↓" : "↑") : ""}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100" {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <i className="bi bi-people text-4xl text-gray-300"></i>
                        <p className="text-gray-500">No candidates found</p>
                        <Link to="/create_candidate" className="text-teal-500 hover:underline">
                          Add your first candidate
                        </Link>
                      </div>
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr className="hover:bg-teal-50/30 transition-colors" {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td className="px-6 py-4 text-sm text-gray-700" {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {page.length > 0 && (
            <div className="p-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{pageIndex * pageSize + 1}</span> to{" "}
                <span className="font-medium">{Math.min((pageIndex + 1) * pageSize, data.length)}</span>{" "}
                of <span className="font-medium">{data.length}</span> results
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button onClick={() => previousPage()} disabled={!canPreviousPage} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {pageOptions.slice(Math.max(0, pageIndex - 2), Math.min(pageOptions.length, pageIndex + 3)).map((pg) => (
                    <button
                      key={pg}
                      onClick={() => gotoPage(pg)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${pageIndex === pg ? "bg-teal-500 text-white" : "hover:bg-gray-100 text-gray-600"}`}
                    >
                      {pg + 1}
                    </button>
                  ))}
                </div>
                <button onClick={() => nextPage()} disabled={!canNextPage} className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
                  Next
                </button>
                <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(Candidate);
